/* eslint-disable no-unused-expressions */
/**
 * TLM Kit
 *
 * Copyright © 2007-present SOHO Systems and Networks. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import { createPath } from 'history/PathUtils';
import App from './components/App';
import createFetch from './createFetch';
import history from './history';
import { updateCustomMeta } from './DOMUtils';
import router from './router';

console.warn = () => {};

// Ensure a CSRF cookie exists before any /graphql call. Pages no longer
// embed the token in HTML — it's issued lazily by GET /api/csrf and stored
// in a `csrf_token` cookie that createFetch reads per-request.
function ensureCsrf() {
  if (typeof document === 'undefined') return Promise.resolve();
  if (/(?:^|;\s*)csrf_token=/.test(document.cookie)) return Promise.resolve();
  return fetch('/api/csrf', { credentials: 'same-origin' }).catch(() => {});
}

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  // Universal HTTP client. Browser mode: createFetch reads csrf_token
  // cookie per request.
  fetch: createFetch(fetch, {
    baseUrl: window.App.apiUrl,
  }),
  flashErrMsgs: window.App.flashErrMsgs,
  user: window.App.user,
};

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance;

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {};
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  const isInitialRender = !action;
  // Only show the full-screen page-loader for the initial SSR→hydration
  // window (when there's no interactive page yet). For client-side
  // navigations we rely on React reconciling in place — the previous
  // page stays visible until the new one is ready, which feels smoother
  // than flashing an 80% white overlay over every click.
  try {
    if (isInitialRender) {
      const loaderEl = document.getElementById('page-loader');
      if (loaderEl) loaderEl.style.visibility = 'visible';
    } else {
      document.body.style.cursor = 'progress';
    }
    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve({
      ...context,
      pathname: location.pathname,
      query: queryString.parse(location.search),
    });

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;
    appInstance = renderReactApp(
      <App context={context}>{route.component}</App>,
      container,
      () => {
        if (isInitialRender) {
          document.title = route.title;
          route.title && updateCustomMeta('og:title', route.title);
          route.ogtitle && updateCustomMeta('og:title', route.ogtitle);
          route.image && updateCustomMeta('og:image', route.image);
          route.image && updateCustomMeta('twitter:image', route.image);
          route.title && updateCustomMeta('twitter:title', route.title);
          route.ogtitle && updateCustomMeta('twitter:title', route.ogtitle);
          route.site && updateCustomMeta('twitter:site', route.site);
          route.card && updateCustomMeta('twitter:card', route.card);
          const elem = document.getElementById('css');
          if (elem) elem.parentNode.removeChild(elem);
          return;
        }

        document.title = route.title;

        route.title && updateCustomMeta('og:title', route.title);
        route.ogtitle && updateCustomMeta('og:title', route.ogtitle);
        route.image && updateCustomMeta('og:image', route.image);
        route.image && updateCustomMeta('twitter:image', route.image);
        route.title && updateCustomMeta('twitter:title', route.title);
        route.ogtitle && updateCustomMeta('twitter:title', route.ogtitle);
        route.site && updateCustomMeta('twitter:site', route.site);
        route.card && updateCustomMeta('twitter:card', route.card);
        // Update necessary tags in <head> at runtime here, ie:
        // updateMeta('keywords', route.keywords);
        // updateCustomMeta('og:url', route.canonicalUrl);
        // updateCustomMeta('og:image', route.imageUrl);
        // updateLink('canonical', route.canonicalUrl);
        // etc.

        let scrollX = 0;
        let scrollY = 0;
        const pos = scrollPositionsHistory[location.key];
        if (pos) {
          scrollX = pos.scrollX;
          scrollY = pos.scrollY;
        } else {
          const targetHash = location.hash.substr(1);
          if (targetHash) {
            const target = document.getElementById(targetHash);
            if (target) {
              scrollY = window.pageYOffset + target.getBoundingClientRect().top;
            }
          }
        }

        // Restore the scroll position if it was saved into the state
        // or scroll to the given #hash anchor
        // or scroll to top of the page
        window.scrollTo(scrollX, scrollY);

        // Google Analytics tracking. Don't send 'pageview' event after
        // the initial rendering, as it was already sent
        if (window.ga) {
          window.ga('send', 'pageview', createPath(location));
        }
      },
    );
    // Page loading is done. Hide the initial-render loader and reset
    // the cursor (the latter is a no-op on subsequent navigations).
    if (document.getElementById('page-loader')) {
      document.getElementById('page-loader').style.visibility = 'hidden';
    }
    document.body.style.cursor = '';
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    // Error while fetch data or page load. Clean up UI state.
    if (document.getElementById('page-loader')) {
      document.getElementById('page-loader').style.visibility = 'hidden';
    }
    document.body.style.cursor = '';

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
ensureCsrf().then(() => onLocationChange(currentLocation));

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}
